import { Group, Text, Box } from '@mantine/core';
import type { Field, FieldState, FieldEvent, WorkAvailability } from '../../types';
import type { SeedingSymbol } from '../../constants';
import { FIELD_STATE_SYMBOL_COLORS } from '../../constants';
import { colors, layout } from '../../theme';
import { FieldStatusBadge } from './FieldStatusBadge';
import { generateDateRange, isSameDate, formatDateMD } from '../../utils/dateUtils';
import { formatFieldArea } from '../../utils/fieldUtils';
import { useCurrentDate } from '../../hooks/useCurrentDate';

interface FieldListItemProps {
  field: Field;
  onClick: () => void;
  onHover: (fieldId: string | null) => void;
  isHovered: boolean;
  /** 圃場のステート（fieldEventServiceから取得） */
  state?: FieldState;
  /** 圃場のイベント一覧（fieldEventServiceから取得） */
  events?: FieldEvent[];
  riskData?: Array<{
    date: Date;
    riskIcon: string;
    riskSymbol: SeedingSymbol | undefined;
    riskLevel: number;
  }>;
  /** 作業可否データ */
  workAvailabilities?: WorkAvailability[];
}

/**
 * 圃場リストアイテムコンポーネント
 * Figmaに準拠したシンプルな行レイアウト
 * 左側: 圃場名+ステータスバッジ+地区名|面積
 * 右側: 播種バー+日付+◯✗△記号グリッド
 */
export function FieldListItem({
  field,
  onClick,
  onHover,
  isHovered,
  state,
  events = [],
  riskData = [],
  workAvailabilities = [],
}: FieldListItemProps) {
  // デバッグ日付対応の「今日」
  const today = useCurrentDate();
  // 今後7日間の日付を生成
  const next7Days = generateDateRange(today, 7);

  // 日付に対応するリスクデータを取得
  const getRiskForDate = (date: Date) => {
    return riskData.find((risk) => isSameDate(new Date(risk.date), date));
  };

  return (
    <Box
      style={{
        cursor: 'pointer',
        backgroundColor: isHovered ? colors.gray[50] : colors.background.card,
        transition: 'background-color 0.15s ease',
        borderBottom: `1px solid ${layout.border.color}`,
        padding: '12px 16px',
      }}
      onClick={onClick}
      onMouseEnter={() => onHover(field.id)}
      onMouseLeave={() => onHover(null)}
    >
      <Group wrap="nowrap" gap="md" align="center">
        {/* 左側: 圃場情報 */}
        <Box style={{ flex: '0 0 auto', minWidth: 200 }}>
          <Group gap="xs" align="center" mb={4}>
            <Text size="xl" fw={700}>
              {field.name}
            </Text>
            {state && <FieldStatusBadge state={state} events={events} />}
          </Group>
          <Text size="sm" c="dimmed">
            {field.district.name} | {formatFieldArea(field)}
          </Text>
        </Box>

        {/* 右側: 播種バー + 日付・記号グリッド */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={0} wrap="nowrap" justify="flex-end" align="stretch">
            {/* 日付・記号グリッド */}
            {next7Days.map((date, index) => {
              const risk = getRiskForDate(date);
              const riskSymbol = risk?.riskSymbol || '–';
              const wa = workAvailabilities.find((w) => isSameDate(new Date(w.date), date));
              const workLevel = wa?.level ?? 0;
              const day = date.getDate();
              // 初日は常にmm/dd、2日目以降は月が変わった時だけmm/dd
              const prevDate = index > 0 ? next7Days[index - 1] : null;
              const showFullDate =
                index === 0 || (prevDate && date.getMonth() !== prevDate.getMonth());
              const dateLabel = showFullDate ? formatDateMD(date) : String(day);

              // 作業可否による背景スタイル（0=可、1=不可）
              const workBgStyle =
                workLevel === 1 ? { backgroundColor: colors.workAvailability.unavailable } : {};

              return (
                <Box
                  key={index}
                  style={{
                    width: 48,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderLeft: `1px solid ${layout.border.color}`,
                    ...workBgStyle,
                  }}
                >
                  {/* 日付ヘッダー */}
                  <Box
                    style={{
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {index === 0 ? (
                      <Box
                        style={{
                          backgroundColor: colors.gray[200],
                          color: colors.gray[600],
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: '12pt',
                          fontWeight: 400,
                        }}
                      >
                        {dateLabel}
                      </Box>
                    ) : (
                      <Text fw={400} style={{ fontSize: '12pt', color: colors.gray[600] }}>
                        {dateLabel}
                      </Text>
                    )}
                  </Box>

                  {/* 記号 */}
                  <Box
                    style={{
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      fw={700}
                      style={{
                        fontSize: '1.25rem',
                        color:
                          riskSymbol === '–'
                            ? colors.gray[400]
                            : ((state && FIELD_STATE_SYMBOL_COLORS[state]) ?? colors.gray[400]),
                      }}
                    >
                      {riskSymbol}
                    </Text>
                  </Box>
                </Box>
              );
            })}

            {/* グリッド右端の罫線 */}
            <Box style={{ borderLeft: `1px solid ${layout.border.color}`, width: 0 }} />
          </Group>
        </Box>
      </Group>
    </Box>
  );
}
